import { ReactNode, SVGProps } from "react";
import { toast as sonnerToast } from "sonner";

const Close = ( props: SVGProps<any> ) => {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" {...props}>
            <path
                d="M14.25 4.8075L13.1925 3.75L9 7.9425L4.8075 3.75L3.75 4.8075L7.9425 9L3.75 13.1925L4.8075 14.25L9 10.0575L13.1925 14.25L14.25 13.1925L10.0575 9L14.25 4.8075Z"
                fill="currentColor"
            />
        </svg>
    );
};
const SuccessIcon = ( props: SVGProps<any> ) => {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" {...props}>
            <g clipPath="url(#clip0_11639_10850)">
                <path
                    d="M8.00004 14.6666C9.84097 14.6666 11.5076 13.9204 12.7141 12.714C13.9205 11.5075 14.6667 9.84085 14.6667 7.99992C14.6667 6.15899 13.9205 4.49232 12.7141 3.28587C11.5076 2.07945 9.84097 1.33325 8.00004 1.33325C6.15911 1.33325 4.49244 2.07945 3.28599 3.28587C2.07957 4.49232 1.33337 6.15899 1.33337 7.99992C1.33337 9.84085 2.07957 11.5075 3.28599 12.714C4.49244 13.9204 6.15911 14.6666 8.00004 14.6666Z"
                    fill="#09C285"
                    stroke="#09C285"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                />
                <path
                    d="M5.33337 8L7.33337 10L11.3334 6"
                    stroke="white"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </g>
            <defs>
                <clipPath id="clip0_11639_10850">
                    <rect width="16" height="16" fill="white" />
                </clipPath>
            </defs>
        </svg>
    );
};
const ErrorIcon = ( props: SVGProps<any> ) => {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" {...props}>
            <g clipPath="url(#clip0_11639_10859)">
                <path
                    d="M8.00004 14.6666C9.84097 14.6666 11.5076 13.9204 12.7141 12.714C13.9205 11.5075 14.6667 9.84085 14.6667 7.99992C14.6667 6.15899 13.9205 4.49232 12.7141 3.28587C11.5076 2.07945 9.84097 1.33325 8.00004 1.33325C6.15911 1.33325 4.49244 2.07945 3.28599 3.28587C2.07957 4.49232 1.33337 6.15899 1.33337 7.99992C1.33337 9.84085 2.07957 11.5075 3.28599 12.714C4.49244 13.9204 6.15911 14.6666 8.00004 14.6666Z"
                    fill="#E23939"
                    stroke="#E23939"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                />
                <rect width="2" height="2" rx="1" transform="matrix(-1 0 0 1 9 10.877)" fill="white" />
                <rect width="2" height="6.63122" rx="1" transform="matrix(-1 0 0 1 9 3.12305)" fill="white" />
            </g>
            <defs>
                <clipPath id="clip0_11639_10859">
                    <rect width="16" height="16" fill="white" />
                </clipPath>
            </defs>
        </svg>
    );
};
const iconMap = { success: SuccessIcon, error: ErrorIcon, info: SuccessIcon };

type ToastOptions = Omit<ToastProps, "id">;

export function showToast( props: ToastOptions ) {
    return sonnerToast.custom( id => {
        return (
            <div className="pointer-events-auto flex w-full justify-center">
                <Toast id={id} {...props} />
            </div>
        );
    } );
}

const createToast = ( type: ToastProps["type"] ) => {
    return ( titleOrOptions: string | ToastOptions, description?: string ) => {
        if ( typeof titleOrOptions === "string" ) {
            return showToast( {
                title: description ? titleOrOptions : undefined,
                description: description || titleOrOptions,
                type,
            } );
        }
        return showToast( { ...titleOrOptions, type } );
    };
};

export const toast = { success: createToast( "success" ), error: createToast( "error" ), info: createToast( "info" ) };

function Toast( props: ToastProps ) {
    const { title = "Friendly Reminder", description = "toast", onClose, id, type = "success" } = props;
    const IconComponent = iconMap[type];
    return (
        <div
            className="pointer-events-auto rounded-[8px] bg-surface-card shadow-lg ring-1 ring-stroke-subtle items-center p-[16px] w-[343px]"
            onPointerDown={( event ) => event.stopPropagation()}
            onClick={( event ) => event.stopPropagation()}
        >
            <div className="flex items-center gap-[8px]">
                <IconComponent className="size-[16px]"></IconComponent>
                <div className="text-[14px] font-medium text-t1">{title}</div>
                <button
                    type="button"
                    aria-label="Close toast"
                    className="ml-auto flex size-lg items-center justify-center text-t1"
                    onPointerDown={( event ) => event.stopPropagation()}
                    onClick={( event ) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onClose?.();
                        sonnerToast.dismiss( id );
                    }}
                >
                    <Close className="size-lg" />
                </button>
            </div>
            <p className="mt-2 text-xs text-t3 font-medium">{description}</p>
        </div>
    );
}

interface ToastProps {
    id: string | number;
    title?: string;
    description: ReactNode;
    onClose?: () => void;
    type?: "success" | "error" | "info";
}
